import { palette, shades } from "lib/miolo";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import orderBy from "lodash/orderBy";
import tinycolor from "tinycolor2";
import TextTooltip from "components/shared/TextTooltip";

interface IFamilyRelationshipsProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

export default function FamilyRelationships({ issuer }: IFamilyRelationshipsProps) {
  return (
    <div
      css={{
        padding: 8,
        overflow: "hidden",
        backgroundColor: palette.white,
        borderRadius: 4,
      }}
    >
      <table
        css={{
          minWidth: "100%",
          fontSize: 14,
          fontFamily: "museo-sans-condensed",
          lineHeight: 1.33,
          letterSpacing: 0.6,
          textAlign: "left",
          textIndent: 0,
          borderColor: "inherit",
          borderCollapse: "collapse",
          "& > thead": {
            backgroundColor: palette.white,
          },
          "& th": {
            padding: "12px 16px",
            color: shades.greys.variants[2],
            fontWeight: 600,
            textTransform: "uppercase",
          },
          "& tr > td": {
            padding: "12px 16px",
          },
          "& > tbody": {
            "& tr": {
              borderColor: palette.fiord,
              borderTopWidth: 1,
              "&:hover": {
                backgroundColor: tinycolor(palette.catskillWhite).lighten(2).toHexString(),
                "& td:first-of-type": {
                  borderTopLeftRadius: 6,
                  borderBottomLeftRadius: 6,
                },
                "& td:last-of-type": {
                  borderTopRightRadius: 6,
                  borderBottomRightRadius: 6,
                },
              },
            },
          },
          "& tbody > tr": {
            borderTop: `solid 1px ${tinycolor(palette.mystic).lighten(3).toHexString()}`,
            borderBottom: "none",
            "&:hover, &:hover + tr": {
              borderTop: "solid 1px transparent",
            },
          },
        }}
      >
        <thead>
          <tr>
            <th>Nome</th>
            <th>Pessoa Relacionada</th>
            <th>Parentesco</th>
          </tr>
        </thead>

        <tbody>
          {orderBy(issuer.familyRelationships, ["name"], ["asc"]).map((node, index) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${node.name}/${index}`}>
                <td>
                  <div>
                    {node.position ? (
                      <TextTooltip
                        id={`FamilyRelationships/${node.name}/${index}/position`}
                        placement="top"
                        content={node.position}
                        style={{
                          padding: 12,
                          maxWidth: 320,
                          textAlign: "center",
                        }}
                      >
                        {({ accessibility, bind, ref }) => (
                          <span
                            {...accessibility}
                            {...bind}
                            ref={ref}
                            css={{
                              fontSize: 16,
                              fontWeight: 500,
                              color: shades.greys.variants[2],
                            }}
                          >
                            {node.name}
                          </span>
                        )}
                      </TextTooltip>
                    ) : (
                      <span
                        css={{
                          fontSize: 16,
                          fontWeight: 500,
                          color: shades.greys.variants[2],
                        }}
                      >
                        {node.name}
                      </span>
                    )}
                  </div>

                  <div>
                    <span css={{ fontSize: 14, color: shades.greys.variants[3] }}>
                      {node.issuerSubsidiaryOrController}
                    </span>
                  </div>
                </td>

                <td>
                  <div>
                    <TextTooltip
                      id={`FamilyRelationships/${node.name}/${index}/position`}
                      placement="top"
                      content={node.relatedPersonPosition}
                      style={{
                        padding: 12,
                        maxWidth: 320,
                        textAlign: "center",
                      }}
                    >
                      {({ accessibility, bind, ref }) => (
                        <span
                          {...accessibility}
                          {...bind}
                          ref={ref}
                          css={{
                            fontSize: 16,
                            fontWeight: 500,
                            color: shades.greys.variants[2],
                          }}
                        >
                          {node.relatedPersonName}
                        </span>
                      )}
                    </TextTooltip>
                  </div>

                  <div>
                    <span css={{ fontSize: 14, color: shades.greys.variants[3] }}>
                      {node.relatedPersonIssuerSubsidiaryOrController}
                    </span>
                  </div>
                </td>

                <td>
                  <span
                    css={{
                      fontSize: 16,
                      color: shades.greys.variants[2],
                    }}
                  >
                    {node.relationship}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
